import React, { useState, useRef, useEffect } from 'react'
import { StyleSheet, View } from 'react-native'
import AppPopover from '../../UIComponents/ModalShell/AppPopover'

import PrivacyButton from '../../UIComponents/FloatModals/PrivacyModal/PrivacyButton'
import HighlightButton from '../../UIComponents/FloatModals/HighlightColorModal/HighlightButton'
import TaskMoreButton from '../../UIComponents/FloatModals/MorePopupsOfEditModals/Tasks/TaskMoreButton'
import DueDateButton from '../../UIControls/DueDateButton'
import CommentButton from '../../UIControls/CommentButton'
import FollowUpButton from '../../UIControls/FollowUpButton'
import TranscribeButton from '../../UIControls/TranscribeButton'
import { FEED_PUBLIC_FOR_ALL, FEED_TASK_OBJECT_TYPE } from '../../Feeds/Utils/FeedsConstants'
import { translate } from '../../../i18n/TranslationService'
import { useSelector } from 'react-redux'
import OpenDvButton from './OpenDvButton'
import GhostButton from '../../UIControls/GhostButton'
import { updateFocusedTask } from '../../../utils/backends/Tasks/tasksFirestore'
import Hotkeys from 'react-hot-keys'
import { execShortcutFn, popoverToTop, popoverToTopContainerStyle } from '../../../utils/HelperFunctions'
import { colors } from '../../styles/global'

import TaskParentGoalModal from '../../UIComponents/FloatModals/TaskParentGoalModal/TaskParentGoalModal'
import { Keyboard } from 'react-native'
import Backend from '../../../utils/BackendBridge'
import { objectIsPublicForLoggedUser } from '../../TaskListView/Utils/TasksHelper'
import { isInboxSummaryGmailTask } from '../../../utils/Gmail/gmailTaskUtils'
import { prepareTaskFocusChange } from '../../../utils/taskFocusInteraction'
import useFloatPopupLock from '../../../hooks/useFloatPopupLock'

export default function SecondaryButtonsArea({
    tmpTask,
    hasName,
    showButtonSpace,
    isSuggestedTask,
    adding,
    projectId,
    isObservedTask,
    isToReviewTask,
    accessGranted,
    loggedUserCanUpdateObject,
    isAssistant,
    newTaskInFocus,
    setNewTaskInFocus,
    onOpenDetailedView,
    setDueDateBeforeSave,
    setToBacklogBeforeSave,
    onDismissPopup,
    setPrivacyBeforeSave,
    setCommentBeforeSave,
    setEstimationBeforeSave,
    setFollowUpBeforeSave,
    setDescriptionBeforeSave,
    setParentGoalBeforeSave,
    dismissEditMode,
    setRecurrenceBeforeSave,
    setPriorityBeforeSave,
    setTempAutoEstimation,
    isPending,
    parentInTaskOutOfOpen,
    createSubtask,
}) {
    const smallScreen = useSelector(state => state.smallScreen)
    const loggedUserId = useSelector(state => state.loggedUser.uid)
    const taskViewToggleSection = useSelector(state => state.taskViewToggleSection)
    const rawInFocusTaskId = useSelector(state => state.loggedUser.inFocusTaskId)
    const optimisticFocusTaskId = useSelector(state => state.optimisticFocusTaskId)
    const optimisticFocusActive = useSelector(state => state.optimisticFocusActive)
    const inFocusTaskId = optimisticFocusActive ? optimisticFocusTaskId : rawInFocusTaskId
    const taskIsFocused = adding ? newTaskInFocus === true : !!tmpTask.id && inFocusTaskId === tmpTask.id
    const blockShortcuts = useSelector(state => state.blockShortcuts)
    const isLockedGmailTask = isInboxSummaryGmailTask(tmpTask)

    const buttonItemStyle = { marginRight: smallScreen ? 8 : 4 }

    const loggedSetDueDateBeforeSave = (task, date, isObserved) => {
        setDueDateBeforeSave(date, isObserved)
    }

    const loggedSetToBacklogBeforeSave = (task, isObserved) => {
        setToBacklogBeforeSave(isObserved)
    }

    const focusTask = event => {
        if (adding) {
            setNewTaskInFocus(currentValue => !currentValue)
            return
        }

        prepareTaskFocusChange(event, dismissEditMode)
        updateFocusedTask(loggedUserId, projectId, taskIsFocused ? null : tmpTask, null, null)
    }

    const focusButtonRef = React.useRef(null)
    const parentGoalButtonRef = useRef(null)

    const [showParentGoalModalUI, setShowParentGoalModalUI] = useState(false)
    const [activeGoalForModal, setActiveGoalForModal] = useState(null)
    const [isLoadingGoal, setIsLoadingGoal] = useState(false)
    const [goalForModalAtOpen, setGoalForModalAtOpen] = useState(null)
    const isUnmountedRef = useRef(false)
    const popupLock = useFloatPopupLock()

    useEffect(() => {
        return () => {
            isUnmountedRef.current = true
        }
    }, [])

    const safeSetShowParentGoalModalUI = value => {
        if (!isUnmountedRef.current) setShowParentGoalModalUI(value)
    }
    const safeSetActiveGoalForModal = value => {
        if (!isUnmountedRef.current) setActiveGoalForModal(value)
    }
    const safeSetIsLoadingGoal = value => {
        if (!isUnmountedRef.current) setIsLoadingGoal(value)
    }
    const safeSetGoalForModalAtOpen = value => {
        if (!isUnmountedRef.current) setGoalForModalAtOpen(value)
    }

    const openParentGoalPopup = async e => {
        e?.preventDefault()
        e?.stopPropagation()
        Keyboard.dismiss()

        if (tmpTask.parentGoalId) {
            safeSetIsLoadingGoal(true)
            try {
                const goal = await Backend.getGoalData(projectId, tmpTask.parentGoalId)
                if (isUnmountedRef.current) return
                if (goal && objectIsPublicForLoggedUser(goal, loggedUserId)) {
                    safeSetGoalForModalAtOpen(goal)
                    safeSetActiveGoalForModal(goal)
                } else {
                    safeSetGoalForModalAtOpen(null)
                    safeSetActiveGoalForModal(null)
                }
            } catch (error) {
                console.error('Error fetching parent goal:', error)
                safeSetGoalForModalAtOpen(null)
                safeSetActiveGoalForModal(null)
            }
            safeSetIsLoadingGoal(false)
        } else {
            safeSetGoalForModalAtOpen(null)
            safeSetActiveGoalForModal(null)
        }

        popupLock.acquire()
        safeSetShowParentGoalModalUI(true)
    }

    const hideParentGoalPopup = () => {
        popupLock.release()
        // Persisting a parent-goal change re-buckets the task immediately. That can unmount this
        // editor before TaskParentGoalModal's awaited save resumes and asks us to close. The
        // popup lock is already released by the hook cleanup; do not continue into the stale
        // task-row dismiss callback after unmount.
        if (isUnmountedRef.current) return
        safeSetShowParentGoalModalUI(false)
        // TaskParentGoalModal owns its own popup lock until this state change unmounts it. Closing
        // the editor without forcing it while that lock is still present makes DismissibleItem
        // reject the close, so the placement hold keeps the task under its previous goal and the
        // completed write looks like it did nothing.
        dismissEditMode?.(true)
    }

    const handleSetParentGoal = (goal, goalProjectId) => setParentGoalBeforeSave(goal, goalProjectId)

    return (
        <View style={localStyles.container}>
            <OpenDvButton
                showButtonSpace={showButtonSpace}
                onOpenDetailedView={onOpenDetailedView}
                buttonItemStyle={buttonItemStyle}
                projectId={projectId}
                disabled={(adding && !hasName) || isLoadingGoal}
            />

            {loggedUserCanUpdateObject &&
                !tmpTask.done &&
                taskViewToggleSection !== 'Workflow' &&
                !isLockedGmailTask && (
                    <>
                        {tmpTask.calendarData ? (
                            <TranscribeButton
                                task={tmpTask}
                                projectId={projectId}
                                style={buttonItemStyle}
                                disabled={!hasName || !accessGranted || isLoadingGoal}
                                shortcutText={'R'}
                                onDismissPopup={onDismissPopup}
                            />
                        ) : (
                            <DueDateButton
                                task={tmpTask}
                                projectId={projectId}
                                style={buttonItemStyle}
                                disabled={!hasName || !accessGranted || isLoadingGoal}
                                inEditTask={true}
                                saveDueDateBeforeSaveTask={loggedSetDueDateBeforeSave}
                                setToBacklogBeforeSaveTask={loggedSetToBacklogBeforeSave}
                                onDismissPopup={onDismissPopup}
                                shortcutText={'R'}
                                isObservedTask={isObservedTask}
                            />
                        )}
                    </>
                )}

            {adding ? (
                <PrivacyButton
                    projectId={projectId}
                    object={tmpTask}
                    objectType={FEED_TASK_OBJECT_TYPE}
                    disabled={!hasName || !accessGranted || isLoadingGoal}
                    savePrivacyBeforeSaveObject={setPrivacyBeforeSave}
                    inEditComponent={true}
                    style={buttonItemStyle}
                    shortcutText={'P'}
                />
            ) : (
                <CommentButton
                    style={buttonItemStyle}
                    task={tmpTask}
                    disabled={!hasName || isLoadingGoal}
                    inEditTask
                    saveCommentBeforeSaveTask={setCommentBeforeSave}
                    projectId={projectId}
                    onDismissPopup={onDismissPopup}
                    shortcutText={'C'}
                />
            )}

            {loggedUserCanUpdateObject && (
                <Hotkeys
                    keyName={'alt+f'}
                    disabled={!hasName || !accessGranted || blockShortcuts || isLoadingGoal}
                    onKeyDown={(sht, event) => execShortcutFn(focusButtonRef.current, focusTask, event)}
                    filter={e => true}
                >
                    <GhostButton
                        ref={focusButtonRef}
                        type={'ghost'}
                        icon={'crosshair'}
                        title={!smallScreen ? translate(taskIsFocused ? 'Set out of focus' : 'Set in focus') : null}
                        noBorder={smallScreen}
                        buttonStyle={
                            taskIsFocused
                                ? { ...buttonItemStyle, backgroundColor: colors.Primary050 }
                                : { ...buttonItemStyle, backgroundColor: 'transparent' }
                        }
                        iconColor={taskIsFocused ? colors.Primary100 : colors.Text03}
                        onPress={focusTask}
                        disabled={!hasName || !accessGranted || isLoadingGoal}
                        shortcutText={'F'}
                        accessibilityRole={'button'}
                        accessibilityState={{ selected: taskIsFocused }}
                    />
                </Hotkeys>
            )}

            {loggedUserCanUpdateObject && !isAssistant && !tmpTask.isSubtask && hasName && accessGranted && (
                <AppPopover
                    content={
                        showParentGoalModalUI ? (
                            <TaskParentGoalModal
                                activeGoal={goalForModalAtOpen || activeGoalForModal}
                                setActiveGoal={handleSetParentGoal}
                                projectId={projectId}
                                closeModal={hideParentGoalPopup}
                                notDelayClose={true}
                                ownerId={tmpTask.userId}
                            />
                        ) : null
                    }
                    onClickOutside={hideParentGoalPopup}
                    isOpen={showParentGoalModalUI}
                    position={['bottom', 'left', 'right', 'top']}
                    padding={4}
                    align={'end'}
                    disableReposition={true}
                    contentLocation={popoverToTop}
                    containerStyle={popoverToTopContainerStyle}
                >
                    <Hotkeys
                        keyName={'alt+g'}
                        disabled={!hasName || !accessGranted || blockShortcuts || isLoadingGoal}
                        onKeyDown={(sht, event) =>
                            execShortcutFn(parentGoalButtonRef.current, openParentGoalPopup, event)
                        }
                        filter={e => true}
                    >
                        <GhostButton
                            ref={parentGoalButtonRef}
                            type={'ghost'}
                            icon={isLoadingGoal ? 'loader' : 'target'}
                            title={!smallScreen ? translate('Parent goal') : null}
                            noBorder={smallScreen}
                            buttonStyle={buttonItemStyle}
                            iconColor={colors.Text03}
                            onPress={openParentGoalPopup}
                            disabled={!hasName || !accessGranted || isLoadingGoal}
                            shortcutText={'G'}
                        />
                    </Hotkeys>
                </AppPopover>
            )}

            {!adding && tmpTask.done && loggedUserCanUpdateObject && !isAssistant && (
                <FollowUpButton
                    buttonText={translate('Follow up')}
                    task={tmpTask}
                    projectId={projectId}
                    style={buttonItemStyle}
                    disabled={!hasName || !accessGranted || isLoadingGoal}
                    inEditTask={true}
                    saveDateBeforeSaveTask={setFollowUpBeforeSave}
                    onDismissPopup={onDismissPopup}
                    shortcutText={'J'}
                />
            )}

            {loggedUserCanUpdateObject && (
                <TaskMoreButton
                    editing={!adding}
                    formType={adding ? 'new' : 'edit'}
                    projectId={projectId}
                    task={
                        !tmpTask.parentGoalIsPublicFor ||
                        tmpTask.parentGoalIsPublicFor.includes(FEED_PUBLIC_FOR_ALL) ||
                        tmpTask.parentGoalIsPublicFor.includes(loggedUserId)
                            ? tmpTask
                            : { ...tmpTask, parentGoalId: null, parentGoalIsPublicFor: null }
                    }
                    buttonStyle={buttonItemStyle}
                    saveDescription={setDescriptionBeforeSave}
                    dismissEditMode={dismissEditMode}
                    savePrivacyBeforeSaveObject={setPrivacyBeforeSave}
                    saveRecurrenceBeforeSaveTask={setRecurrenceBeforeSave}
                    setPriorityBeforeSave={setPriorityBeforeSave}
                    disabled={!hasName || !accessGranted || isLoadingGoal}
                    isSuggestedTask={isSuggestedTask}
                    setEstimationBeforeSave={setEstimationBeforeSave}
                    isAssistant={isAssistant}
                    setTempAutoEstimation={setTempAutoEstimation}
                    createSubtask={createSubtask}
                />
            )}
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        flexDirection: 'row',
    },
})
